import { useAppStore } from '@/stores/appStore'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type EarlyCareStateShadow } from '@/lib/db'
import type { EarlyCareState, Person } from '@/lib/types'
import { supabase } from '@/lib/supabase'
import { useQuery } from '@tanstack/react-query'

export function useFocusedPerson(): Person | null {
  const persons = useAppStore((s) => s.persons)
  const focused = useAppStore((s) => s.focusedPersonId)
  if (!focused) return null
  return persons.find((p) => p.id === focused) ?? null
}

export function useEarlyCareState(personId: string | null): EarlyCareState | null {
  const shadow = useLiveQuery<EarlyCareStateShadow | undefined>(
    async () => (personId ? await db.early_care_state_shadow.get(personId) : undefined),
    [personId],
  )

  const query = useQuery({
    enabled: !!personId,
    queryKey: ['early_care_state', personId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('early_care_state')
        .select('*')
        .eq('person_id', personId!)
        .maybeSingle()
      if (error) throw error
      return (data ?? null) as EarlyCareState | null
    },
  })

  if (shadow && shadow.json) {
    try {
      return JSON.parse(shadow.json) as EarlyCareState
    } catch {
      // fall through to network
    }
  }
  return query.data ?? null
}
